import { ApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  EmptyStation,
  type FormActionData,
  GenericField,
  InfoPanel,
  MessageBar,
  type ObjectSchemaDefinition,
  Paragraph,
  Section,
  SingleLineTextField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import {
  ClearMigrationWebHookSecretDocument,
  type ClearMigrationWebHookSecretMutation,
  type ClearMigrationWebHookSecretMutationVariables,
  type IdpUserStore,
  IdpUserStoreDocument,
  type IdpUserStoreQuery,
  type IdpUserStoreQueryVariables,
  type MutationUpdateIdpUserStoreArgs,
  UpdateIdpUserStoreDocument,
  type UpdateIdpUserStoreMutation,
  type UpdateIdpUserStoreMutationVariables,
  useUpdateIdpUserStoreMutation,
} from 'generated/axauth-service';
import { type BreadcrumbResolver } from 'models/breadcrumb';
import { environmentApolloClient } from 'modules/environmentApolloClient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { getManagedServiceAvailabilityMessage } from 'util/managedServiceUtil';
import { validateUrl } from 'util/validationUtil';
import * as Yup from 'yup';
import { ensureError } from '../../../../../common/errors/ensure-error';
import { type SubmitResponse } from './MigrationWebHook.types';

const webhookValidation = Yup.object<ObjectSchemaDefinition>({
  migrationWebhook: Yup.string()
    .required('Migration webhook URL is required')
    .test(
      'migration-webhook-url',
      'Migration webhook must be a valid URL',
      validateUrl,
    ),
});
type FormData = MutationUpdateIdpUserStoreArgs['input']['patch'];

export const MigrationWebHookDetails: React.FC = () => {
  const { userStoreId, envId } = useParams<{
    userStoreId: string;
    envId: string;
  }>();
  const [data, setData] = useState<IdpUserStoreQuery>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const envApolloClient = useRef<ApolloClient<Record<string, unknown>>>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of axauth-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.AXAUTH_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          envApolloClient.current = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
          );

          // Get IdpUserStore data
          const idpUserStoreResult = await envApolloClient.current.query<
            IdpUserStoreQuery,
            IdpUserStoreQueryVariables
          >({
            query: IdpUserStoreDocument,
            variables: {
              id: userStoreId,
            },
            fetchPolicy: 'no-cache',
          });

          setData(idpUserStoreResult.data);
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, userStoreId]);

  const { actions } = useActions(envId, userStoreId);

  const [updateIdpUserStore] = useUpdateIdpUserStoreMutation({
    client: envApolloClient.current,
    fetchPolicy: 'no-cache',
  });

  const saveData = useCallback(
    async (formData: FormData): Promise<SubmitResponse> => {
      envApolloClient.current = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.userServiceAdminBaseUrl).toString(),
      );
      const response = await updateIdpUserStore({
        variables: {
          input: {
            id: userStoreId,
            patch: { migrationWebhook: formData.migrationWebhook },
          },
        },
      });
      return response.data;
    },
    [envId, updateIdpUserStore, userStoreId],
  );

  const Panel: React.FC = () => {
    const { values } = useFormikContext<IdpUserStore>();
    return (
      <InfoPanel>
        <Section>
          <Paragraph title="Tenant ID">{values?.tenantId}</Paragraph>
          <Paragraph title="Environment ID">{values?.environmentId}</Paragraph>
        </Section>
      </InfoPanel>
    );
  };

  const Form: React.FC = () => {
    return (
      <>
        <Field
          name="migrationWebhook"
          label="Migration Webhook URL"
          tooltipContent="A webhook that verifies an end-user's credentials against a legacy identity provider during a lazy credential migration. It is called when the user's password has not yet been re-homed into AxAuth (i.e. no matching user exists yet, or the user was provisioned via session transfer). On success, AxAuth stores the returned credentials and completes sign-in."
          as={SingleLineTextField}
        />
        <GenericField name="secret" label="Migration Webhook Secret">
          {data?.idpUserStore?.migrationWebhookSecretIsSet ? (
            <p>*************************************************</p>
          ) : (
            <p>Not Generated</p>
          )}
        </GenericField>
      </>
    );
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Migration Webhook">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details<FormData>
          defaultTitle="Migration Webhook"
          subtitle="Migration Webhook Details"
          actions={actions}
          validationSchema={webhookValidation}
          initialData={{
            data: data?.idpUserStore,
            loading,
            error,
          }}
          saveData={saveData}
          infoPanel={<Panel />}
        >
          <Form />
        </Details>
      )}
    </>
  );
};

function useActions(
  envId: string,
  userStoreId: string,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const history = useHistory();
  const actions: FormActionData<FormData>[] = [
    {
      label: 'Webhook Secret',
      onActionSelected: () => {
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/migration/details/secret/migration`,
        );
      },
    },
    {
      label: 'Delete',
      confirmationMode: 'Simple',
      onActionSelected: async () => {
        const envApolloClient = environmentApolloClient(
          envId,
          new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
        );
        await envApolloClient.mutate<
          UpdateIdpUserStoreMutation,
          UpdateIdpUserStoreMutationVariables
        >({
          mutation: UpdateIdpUserStoreDocument,
          variables: {
            input: {
              id: userStoreId,
              patch: { migrationWebhook: '' },
            },
          },
        });

        await envApolloClient.mutate<
          ClearMigrationWebHookSecretMutation,
          ClearMigrationWebHookSecretMutationVariables
        >({
          mutation: ClearMigrationWebHookSecretDocument,
          variables: {
            input: { userStoreId: userStoreId },
          },
          fetchPolicy: 'no-cache',
        });
        history.push(
          `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}`,
        );
      },
    },
  ];

  return { actions } as const;
}

export const MigrationWebHookDetailsBreadCrumb: BreadcrumbResolver = () =>
  'Migration Webhook Details';
